import { UserMax } from "../handlers/types";
import { Character, CharacterData } from "../scripts/game/character/Character";

const STORAGE_KEYS = {
  CHARACTER: "game_character",
  USER: "user",
  FIRST_PLAY_COMPLETED: "first_play_completed",
  LAST_VISIT_TIME: "last_visit_time",
  SPECIAL_DEALS_SHOWN_TODAY: "special_deals_shown_today",
  INVITE_PROMO_SHOWN_TODAY: "invite_promo_shown_today",
  BOOK_PROMO_SHOWN_TODAY: "book_promo_shown_today",
  // Settings
  SETTINGS_MUSIC_ENABLED: "settings_music_enabled",
  SETTINGS_SOUNDS_ENABLED: "settings_sounds_enabled",
  SETTINGS_HAPTICS_ENABLED: "settings_haptics_enabled",
  // (deprecated) ONBOARDING_MANA_GUIDE_ACTIVE: no longer used; guide is part of onboarding modal flow
} as const;

export class StorageService {
  /**
   * Keys for one-time UI hints/notifications. When a hint is dismissed,
   * we set the corresponding key in localStorage to "true" and never show it again.
   */
  static readonly ONE_TIME_HINT_KEYS = {
    MANA_TAB: "hint_mana_tab_dismissed",
    MANA_START_BUTTON: "hint_mana_start_button_dismissed",
    MAIN_BOOKS_BUTTON: "hint_main_books_button_dismissed",
    FIRST_BOOK_BUY_BUTTON: "hint_first_book_buy_button_dismissed",
  } as const;

  /**
   * Returns true if the given one-time hint has already been dismissed.
   */
  static isOneTimeHintDismissed(key: string): boolean {
    try {
      return localStorage.getItem(key) === "true";
    } catch (error) {
      console.error("Failed to read one-time hint state:", error);
      return false;
    }
  }

  /**
   * Mark the given one-time hint as dismissed.
   */
  static dismissOneTimeHint(key: string): void {
    try {
      localStorage.setItem(key, "true");
    } catch (error) {
      console.error("Failed to write one-time hint state:", error);
    }
  }
  /**
   * Save a single character to local storage
   */
  static saveCharacter(character: Character): void {
    try {
      const serializedCharacter = JSON.stringify(character.toJSON());
      localStorage.setItem(STORAGE_KEYS.CHARACTER, serializedCharacter);
    } catch (error) {
      console.error("Failed to save character:", error);
    }
  }

  /**
   * Load a single character from local storage
   */
  static loadCharacter(): Character | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.CHARACTER);
      if (!saved) return null;
      const characterData = JSON.parse(saved) as CharacterData;
      return new Character(characterData);
    } catch (error) {
      console.error("Failed to load character:", error);
      return null;
    }
  }

  static saveUser(user: UserMax): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error("Failed to save user:", error);
    }
  }

  static loadUser(): UserMax | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.USER);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("Failed to load user:", error);
      return null;
    }
  }

  /**
   * Check if the user has completed their first play
   */
  static hasCompletedFirstPlay(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEYS.FIRST_PLAY_COMPLETED) === "true";
    } catch (error) {
      console.error("Failed to check first play status:", error);
      return false;
    }
  }

  /**
   * Mark that the user has completed their first play
   */
  static setFirstPlayCompleted(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.FIRST_PLAY_COMPLETED, "true");
    } catch (error) {
      console.error("Failed to set first play status:", error);
    }
  }

  /**
   * Get the last visit time
   */
  static getLastVisitTime(): number | null {
    try {
      const lastVisit = localStorage.getItem(STORAGE_KEYS.LAST_VISIT_TIME);
      return lastVisit ? parseInt(lastVisit, 10) : null;
    } catch (error) {
      console.error("Failed to get last visit time:", error);
      return null;
    }
  }

  /**
   * Update the last visit time to current time
   */
  static updateLastVisitTime(): void {
    try {
      const currentTime = Date.now();
      localStorage.setItem(
        STORAGE_KEYS.LAST_VISIT_TIME,
        currentTime.toString()
      );
    } catch (error) {
      console.error("Failed to update last visit time:", error);
    }
  }

  /**
   * Check if user should see special deals (24+ hours since last visit)
   * @returns true if user should see special deals
   */
  static shouldShowSpecialDeals(): boolean {
    try {
      const lastVisit = this.getLastVisitTime();
      const now = Date.now();

      // If no last visit recorded, don't show special deals (first time user)
      if (!lastVisit) {
        return false;
      }

      // Check if 24 hours (86400000 ms) have passed
      const timeDifference = now - lastVisit;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      return timeDifference >= twentyFourHours;
    } catch (error) {
      console.error("Failed to check special deals eligibility:", error);
      return false;
    }
  }

  /**
   * Check if special deals have been shown today
   */
  static hasSpecialDealsBeenShownToday(): boolean {
    try {
      const shownToday = localStorage.getItem(
        STORAGE_KEYS.SPECIAL_DEALS_SHOWN_TODAY
      );
      if (!shownToday) return false;

      const shownDate = new Date(parseInt(shownToday, 10));
      const today = new Date();

      // Check if it's the same day
      return (
        shownDate.getDate() === today.getDate() &&
        shownDate.getMonth() === today.getMonth() &&
        shownDate.getFullYear() === today.getFullYear()
      );
    } catch (error) {
      console.error("Failed to check if special deals shown today:", error);
      return false;
    }
  }

  /**
   * Mark special deals as shown today
   */
  static markSpecialDealsShownToday(): void {
    try {
      const now = Date.now();
      localStorage.setItem(
        STORAGE_KEYS.SPECIAL_DEALS_SHOWN_TODAY,
        now.toString()
      );
    } catch (error) {
      console.error("Failed to mark special deals as shown:", error);
    }
  }

  /**
   * Check if invite promo has been shown today
   */
  static hasInvitePromoBeenShownToday(): boolean {
    try {
      const shownToday = localStorage.getItem(
        STORAGE_KEYS.INVITE_PROMO_SHOWN_TODAY
      );
      if (!shownToday) return false;

      const shownDate = new Date(parseInt(shownToday, 10));
      const today = new Date();

      return (
        shownDate.getDate() === today.getDate() &&
        shownDate.getMonth() === today.getMonth() &&
        shownDate.getFullYear() === today.getFullYear()
      );
    } catch (error) {
      console.error("Failed to check if invite promo shown today:", error);
      return false;
    }
  }

  /**
   * Mark invite promo as shown today
   */
  static markInvitePromoShownToday(): void {
    try {
      const now = Date.now();
      localStorage.setItem(
        STORAGE_KEYS.INVITE_PROMO_SHOWN_TODAY,
        now.toString()
      );
    } catch (error) {
      console.error("Failed to mark invite promo as shown:", error);
    }
  }

  /**
   * Check if book promo has been shown today
   */
  static hasBookPromoBeenShownToday(): boolean {
    try {
      const shownToday = localStorage.getItem(
        STORAGE_KEYS.BOOK_PROMO_SHOWN_TODAY
      );
      if (!shownToday) return false;

      const shownDate = new Date(parseInt(shownToday, 10));
      const today = new Date();

      return (
        shownDate.getDate() === today.getDate() &&
        shownDate.getMonth() === today.getMonth() &&
        shownDate.getFullYear() === today.getFullYear()
      );
    } catch (error) {
      console.error("Failed to check if book promo shown today:", error);
      return false;
    }
  }

  /**
   * Mark book promo as shown today
   */
  static markBookPromoShownToday(): void {
    try {
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.BOOK_PROMO_SHOWN_TODAY, now.toString());
    } catch (error) {
      console.error("Failed to mark book promo as shown:", error);
    }
  }

  /**
   * Initialize session tracking - should be called when app starts
   */
  static initializeSession(): { shouldShowSpecialDeals: boolean } {
    try {
      const shouldShow =
        this.shouldShowSpecialDeals() && !this.hasSpecialDealsBeenShownToday();

      // Update last visit time for next session
      this.updateLastVisitTime();

      return { shouldShowSpecialDeals: shouldShow };
    } catch (error) {
      console.error("Failed to initialize session:", error);
      // Update last visit time even if check failed
      this.updateLastVisitTime();
      return { shouldShowSpecialDeals: false };
    }
  }
}

// Settings helpers
export class SettingsStorageService {
  static isMusicEnabled(): boolean {
    try {
      const value = localStorage.getItem(STORAGE_KEYS.SETTINGS_MUSIC_ENABLED);
      return value === null ? true : value === "true";
    } catch (error) {
      return true;
    }
  }

  static setMusicEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.SETTINGS_MUSIC_ENABLED,
        enabled ? "true" : "false"
      );
    } catch (error) {}
  }

  static isSoundsEnabled(): boolean {
    try {
      const value = localStorage.getItem(STORAGE_KEYS.SETTINGS_SOUNDS_ENABLED);
      return value === null ? true : value === "true";
    } catch (error) {
      return true;
    }
  }

  static setSoundsEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.SETTINGS_SOUNDS_ENABLED,
        enabled ? "true" : "false"
      );
    } catch (error) {}
  }

  static isHapticsEnabled(): boolean {
    try {
      const value = localStorage.getItem(STORAGE_KEYS.SETTINGS_HAPTICS_ENABLED);
      return value === null ? true : value === "true";
    } catch (error) {
      return true;
    }
  }

  static setHapticsEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.SETTINGS_HAPTICS_ENABLED,
        enabled ? "true" : "false"
      );
    } catch (error) {}
  }
}
